// Graph Builder generator: pictographs, bar graphs, line graphs, means, and one honest-axis
// question. Data sets are small and about the Town.

import { int, pick, shuffle, type Rng } from '@kit/rng';

export type GraphKind = 'read-picto' | 'read-bar' | 'build-bar' | 'build-picto' | 'mode' | 'more-than' | 'line-read' | 'line-jump' | 'mean' | 'two-series' | 'truth';

export interface DataSet { title: string; unit: string; categories: string[]; icon: 'cupcake' | 'rock' | 'book' | 'letter' | 'pet' }

export interface GraphProblem {
  kind: GraphKind;
  set: DataSet;
  values: number[];
  values2?: number[];
  series: [string, string];
  key: number;            // symbols: value per symbol
  scale: number;          // bar/line axis step
  question: string;
  answer: number | string;
  choices: (number | string)[];
  hint: string;
  /** build kinds: she must match these; the graph starts at startValues */
  startValues?: number[];
  /** truth kind: axis start for graph B (graph A starts at 0) */
  axisStartB?: number;
  id: string;
}

const SETS: DataSet[] = [
  { title: 'Cupcakes Rosa sold', unit: 'cupcakes', categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], icon: 'cupcake' },
  { title: "Rocks in Gus's shop", unit: 'rocks', categories: ['Igneous', 'Sedimentary', 'Metamorphic'], icon: 'rock' },
  { title: 'Books Mr. Quill shelved', unit: 'books', categories: ['Stories', 'Science', 'Poems', 'Comics'], icon: 'book' },
  { title: 'Letters Dot delivered', unit: 'letters', categories: ['Oak St', 'Pine St', 'Elm St', 'Bay St'], icon: 'letter' },
  { title: 'Pets in the Town', unit: 'pets', categories: ['Stone', 'Leaf', 'Spark', 'Wave'], icon: 'pet' },
];

const DAYS: DataSet = SETS[0];

function values(rng: Rng, n: number, lo: number, hi: number, step: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(Math.round(int(rng, lo, hi) / step) * step);
  return out;
}

function distinctMax(vals: number[]): boolean {
  const m = Math.max(...vals);
  return vals.filter(v => v === m).length === 1;
}

function choiceNums(answer: number, rng: Rng, step: number): number[] {
  const set = new Set<number>([answer]);
  let guard = 0;
  while (set.size < 4 && guard++ < 60) { const v = answer + (int(rng, 1, 3) * step) * (rng() < 0.5 ? -1 : 1); if (v >= 0) set.add(v); }
  return shuffle(rng, [...set]);
}

export function makeGraph(level: number, rng: Rng, index: number): GraphProblem {
  const L = Math.max(1, Math.min(10, level));
  if (L <= 2) return readPicto(rng, L === 1 ? 1 : 2);
  if (L <= 4) return index % 2 === 0 ? readBar(rng, pick(rng, [2, 5])) : buildBar(rng, pick(rng, [2, 5]));
  if (L <= 6) return index % 3 === 0 ? buildPicto(rng) : index % 3 === 1 ? mode(rng) : moreThan(rng);
  if (L <= 8) return index % 3 === 0 ? lineJump(rng) : index % 3 === 1 ? lineRead(rng) : mean(rng);
  return index % 2 === 0 ? twoSeries(rng) : truth(rng);
}

function readPicto(rng: Rng, key: number): GraphProblem {
  const set = pick(rng, SETS);
  const n = Math.min(4, set.categories.length);
  const cats = set.categories.slice(0, n);
  let vals: number[];
  do { vals = values(rng, n, key, key * 6, key); } while (!distinctMax(vals));
  const which = int(rng, 0, n - 1);
  if (rng() < 0.5) {
    return { kind: 'read-picto', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key, scale: key, question: `How many ${set.unit} for ${cats[which]}?`, answer: vals[which], choices: choiceNums(vals[which], rng, key), hint: key === 1 ? `Count the pictures in the ${cats[which]} row.` : `Each picture means ${key}. Count the pictures in the ${cats[which]} row and multiply by ${key}.`, id: `gp:${vals.join('-')}` };
  }
  const maxI = vals.indexOf(Math.max(...vals));
  return { kind: 'read-picto', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key, scale: key, question: `Which has the most ${set.unit}?`, answer: cats[maxI], choices: shuffle(rng, cats.slice()), hint: 'The longest row of pictures is the most.', id: `gp:${vals.join('-')}` };
}

function readBar(rng: Rng, scale: number): GraphProblem {
  const set = pick(rng, SETS);
  const n = Math.min(4, set.categories.length);
  const cats = set.categories.slice(0, n);
  let vals: number[];
  do { vals = values(rng, n, scale, scale * 8, scale); } while (!distinctMax(vals));
  const which = int(rng, 0, n - 1);
  const r = rng();
  if (r < 0.5) return { kind: 'read-bar', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key: 1, scale, question: `How many ${set.unit} for ${cats[which]}?`, answer: vals[which], choices: choiceNums(vals[which], rng, scale), hint: `Follow the top of the ${cats[which]} bar across to the numbers. The numbers go up by ${scale}.`, id: `gb:${vals.join('-')}` };
  const minI = vals.indexOf(Math.min(...vals));
  const maxI = vals.indexOf(Math.max(...vals));
  const least = r < 0.75 && vals.filter(v => v === vals[minI]).length === 1;
  return { kind: 'read-bar', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key: 1, scale, question: least ? `Which has the fewest ${set.unit}?` : `Which has the most ${set.unit}?`, answer: cats[least ? minI : maxI], choices: shuffle(rng, cats.slice()), hint: least ? 'The shortest bar is the fewest.' : 'The tallest bar is the most.', id: `gb:${vals.join('-')}` };
}

function buildBar(rng: Rng, scale: number): GraphProblem {
  const set = pick(rng, SETS);
  const n = Math.min(4, set.categories.length);
  const cats = set.categories.slice(0, n);
  const vals = values(rng, n, scale, scale * 8, scale);
  return { kind: 'build-bar', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key: 1, scale, question: 'Build the bar graph to match the table.', answer: vals.join(','), choices: [], hint: `Tap + or − under each bar until it reaches the number in the table. Each tap is ${scale}.`, startValues: vals.map(() => 0), id: `bb:${vals.join('-')}` };
}

function buildPicto(rng: Rng): GraphProblem {
  const set = pick(rng, SETS);
  const n = Math.min(4, set.categories.length);
  const cats = set.categories.slice(0, n);
  const vals = values(rng, n, 5, 30, 5);
  return { kind: 'build-picto', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key: 5, scale: 5, question: 'Build the pictograph. Each picture stands for 5.', answer: vals.join(','), choices: [], hint: 'Divide each number by 5 to know how many pictures the row needs.', startValues: vals.map(() => 0), id: `bp:${vals.join('-')}` };
}

function mode(rng: Rng): GraphProblem {
  const set = pick(rng, SETS);
  const n = Math.min(4, set.categories.length);
  const cats = set.categories.slice(0, n);
  const m = int(rng, 2, 9);
  const others = shuffle(rng, [1, 2, 3, 4, 5, 6, 7, 8, 9].filter(v => v !== m)).slice(0, n - 2);
  const vals = shuffle(rng, [m, m, ...others]);
  return { kind: 'mode', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key: 1, scale: 1, question: `Which number of ${set.unit} shows up most often (the mode)?`, answer: m, choices: shuffle(rng, [...new Set(vals)]).slice(0, 4), hint: 'Look for two bars that are exactly the same height.', id: `md:${vals.join('-')}` };
}

function moreThan(rng: Rng): GraphProblem {
  const set = pick(rng, SETS);
  const n = Math.min(4, set.categories.length);
  const cats = set.categories.slice(0, n);
  const vals = values(rng, n, 2, 20, 2);
  let a = int(rng, 0, n - 1), b = int(rng, 0, n - 1);
  if (a === b) b = (a + 1) % n;
  if (vals[a] === vals[b]) vals[a] += 2;
  const [hi, lo] = vals[a] > vals[b] ? [a, b] : [b, a];
  const diff = vals[hi] - vals[lo];
  return { kind: 'more-than', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key: 1, scale: 2, question: `How many more ${set.unit} does ${cats[hi]} have than ${cats[lo]}?`, answer: diff, choices: choiceNums(diff, rng, 2), hint: `Read both bars, then subtract: ${vals[hi]} − ${vals[lo]}.`, id: `mt:${vals.join('-')}` };
}

function lineRead(rng: Rng): GraphProblem {
  const vals = values(rng, 5, 5, 40, 5);
  const which = int(rng, 0, 4);
  return { kind: 'line-read', set: DAYS, values: vals, series: ['cupcakes', ''], key: 1, scale: 5, question: `How many cupcakes did Rosa sell on ${DAYS.categories[which]}?`, answer: vals[which], choices: choiceNums(vals[which], rng, 5), hint: `Find ${DAYS.categories[which]} along the bottom, go up to the dot, then across to the numbers.`, id: `lr:${vals.join('-')}` };
}

function lineJump(rng: Rng): GraphProblem {
  let vals: number[];
  let best = 0;
  let unique = false;
  let guard = 0;
  do {
    vals = values(rng, 5, 5, 40, 5);
    const jumps = vals.slice(1).map((v, i) => Math.abs(v - vals[i]));
    const m = Math.max(...jumps);
    best = jumps.indexOf(m);
    unique = jumps.filter(j => j === m).length === 1;
  } while (!unique && guard++ < 50);
  const labels = DAYS.categories.slice(1).map((d, i) => `${DAYS.categories[i]} to ${d}`);
  return { kind: 'line-jump', set: DAYS, values: vals, series: ['cupcakes', ''], key: 1, scale: 5, question: 'Between which two days did sales change the most?', answer: labels[best], choices: shuffle(rng, labels.slice()), hint: 'Look for the steepest line between two dots, up or down.', id: `lj:${vals.join('-')}` };
}

function mean(rng: Rng): GraphProblem {
  const set = pick(rng, SETS.filter(s => s.categories.length >= 4));
  const cats = set.categories.slice(0, 4);
  const m = int(rng, 4, 15);
  const d1 = int(rng, 0, 3), d2 = int(rng, 0, 3);
  const vals = shuffle(rng, [m + d1, m - d1, m + d2, m - d2]);
  return { kind: 'mean', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key: 1, scale: 2, question: `What is the mean (average) number of ${set.unit}?`, answer: m, choices: choiceNums(m, rng, 1), hint: `Add all four: ${vals.join(' + ')} = ${vals.reduce((a, b) => a + b, 0)}, then divide by 4.`, id: `mn:${vals.join('-')}` };
}

function twoSeries(rng: Rng): GraphProblem {
  let a: number[], b: number[], best = 0, unique = false, guard = 0;
  do {
    a = values(rng, 5, 5, 40, 5); b = values(rng, 5, 5, 40, 5);
    const diffs = a.map((v, i) => v - b[i]);
    const m = Math.max(...diffs);
    best = diffs.indexOf(m);
    unique = m > 0 && diffs.filter(d => d === m).length === 1;
  } while (!unique && guard++ < 80);
  return { kind: 'two-series', set: DAYS, values: a, values2: b, series: ['Rosa', 'Pip'], key: 1, scale: 5, question: 'Rosa and Pip both sold things this week. On which day did Rosa beat Pip by the most?', answer: DAYS.categories[best], choices: shuffle(rng, DAYS.categories.slice()), hint: 'Find the day where the gap between the two bars is biggest, with Rosa on top.', id: `ts:${a.join('-')}|${b.join('-')}` };
}

function truth(rng: Rng): GraphProblem {
  const set = pick(rng, SETS);
  const cats = set.categories.slice(0, 2);
  const base = int(rng, 40, 60);
  const vals = [base, base + int(rng, 2, 6)];
  const axisStartB = base - 2;
  const bIsTruth = false;
  return { kind: 'truth', set: { ...set, categories: cats }, values: vals, series: [set.unit, ''], key: 1, scale: 10, question: `Both graphs show the same numbers. Which graph shows the real size of the difference between ${cats[0]} and ${cats[1]}?`, answer: bIsTruth ? 'Graph B' : 'Graph A', choices: ['Graph A', 'Graph B'], hint: 'Check where the number line starts. A graph that starts at 0 shows the true size of each bar.', axisStartB, id: `tr:${vals.join('-')}` };
}
